/**
 * Marketing Coupon Strategy
 * Handles MARKETING type coupons from campaign links
 */
import { BaseCouponStrategy } from './base-coupon-strategy';
import { couponTypes, couponSourceTypeMap } from '../helper/constant';
import { processCoupon, getDefaultCouponState } from '../helper/utils';
import _ from 'lodash';

export class MarketingCouponStrategy extends BaseCouponStrategy {
  /**
   * Get coupon type identifier
   * @returns {string} MARKETING coupon type
   */
  getType() {
    return couponTypes.MARKETING;
  }

  /**
   * Calculate discount for MARKETING coupon
   * @param {Object} couponState - Current coupon state
   * @param {Array} selectedPlans - Selected plans array
   * @param {Object} planWithAmountMap - Plan amount mapping
   * @param {Object} context - Additional context
   * @returns {Object} Calculated discount result
   */
  // eslint-disable-next-line no-unused-vars
  calculateDiscount(couponState, selectedPlans, planWithAmountMap, context) {
    const discountPlanList = this.transformMarketingToDiscountList(couponState);

    if (discountPlanList.length === 0) {
      return this.getInitialResult();
    }

    return processCoupon({
      discountPlanList,
      selectedPlanList: selectedPlans,
      planWithAmountMap,
    });
  }

  /**
   * Transform marketing coupon data to discount plan list
   * @param {Object} couponState - Coupon state with discountInfo
   * @returns {Array} List of discount plans
   */
  transformMarketingToDiscountList(couponState) {
    // Merge discount info and extension for plan increase cases (e.g., zoom one pro)
    const fullDiscountInfo = {
      ...couponState.discountInfo,
      ...couponState.discountInfoExtension,
    };

    return _.chain(fullDiscountInfo)
      .keys()
      .map((planCode) => {
        const discountInfo = _.get(fullDiscountInfo, [planCode], {});
        const {
          expireDate,
          discount,
          couponDiscountType: discountType,
          couponPeriod,
          couponDefType = couponTypes.MARKETING,
        } = discountInfo;

        return {
          planCode,
          expireDate,
          discount,
          discountType,
          couponPeriod,
          couponDefType,
        };
      })
      .filter((item) => item.discount != null)
      .value();
  }

  /**
   * Convert campaign coupon result to coupon state
   * @param {Object} marketingCoupon - Marketing coupon data from backend
   * @param {Object} options - Conversion options
   * @returns {Object} Coupon state
   */
  // eslint-disable-next-line no-unused-vars
  convertApiResultToState(marketingCoupon, options = {}) {
    const {
      couponCode,
      discountInfo,
      billingCycle,
      tncMessage,
      multipleTargetSwitch,
      defaultDiscountInfo,
      discountInfoExtension,
      couponSourceType,
      subscriptionLevelAction,
    } = marketingCoupon || {};

    return {
      ...getDefaultCouponState(),
      couponCode,
      tncMessage,
      billingCycle,
      discountInfo: discountInfo || {},
      defaultDiscountInfo,
      discountInfoExtension, // For extension increase/decrease plan
      multipleTargetSwitch, // Switch for default preload source
      couponSourceType: couponSourceType || couponSourceTypeMap.marketing,
      couponType: couponTypes.MARKETING,
      fromPreloadLink: true, // From campaign link
      subscriptionLevelAction, // Subscription level action
    };
  }

  /**
   * Convert partner referral coupon result to coupon state
   * @param {Object} coupon - Partner referral coupon data
   * @param {Object} options - Conversion options
   * @returns {Object} Coupon state
   */
  // eslint-disable-next-line no-unused-vars
  convertPartnerReferToState(coupon, options = {}) {
    const {
      couponCode,
      discountInfo,
      tncMessage,
      multipleTargetSwitch,
      defaultDiscountInfo,
      discountInfoExtension,
      subscriptionLevelAction,
      isPendingCoupon = false,
    } = coupon || {};

    return {
      ...getDefaultCouponState(),
      isPendingCoupon,
      couponCode,
      tncMessage,
      discountInfo: discountInfo || {},
      defaultDiscountInfo,
      discountInfoExtension,
      multipleTargetSwitch,
      couponType: couponTypes.MARKETING,
      couponSourceType: couponSourceTypeMap.partnerRefer,
      fromPreloadLink: true,
      subscriptionLevelAction,
    };
  }

  /**
   * Validate marketing coupon code
   * @param {string} couponCode - Coupon code to validate
   * @param {Object} context - Validation context
   * @returns {Promise<Object>} Validation result
   */
  // eslint-disable-next-line no-unused-vars
  async validate(couponCode, context) {
    return {
      valid: true,
      couponCode,
    };
  }

  /**
   * Check if marketing coupon can be applied
   * @param {Object} context - Application context
   * @returns {boolean} Whether coupon can be applied
   */
  // eslint-disable-next-line no-unused-vars
  canApply(context) {
    // Marketing coupons are typically pre-applied from campaign links
    return true;
  }

  /**
   * Check if coupon is from campaign link
   * @param {Object} couponState - Coupon state
   * @returns {boolean} Whether from campaign link
   */
  isFromCampaignLink(couponState) {
    return couponState?.fromPreloadLink || couponState?.fromCampaignLink;
  }
}

// Export singleton instance
export const marketingCouponStrategy = new MarketingCouponStrategy();
